import UserAccount from '../services/UserAccount';
import { isLiveStreamCustomSource } from './contentSourceType';

/** Tree is a livestream when customSource.type is LiveStream. */
export function isLiveStreamContent(tree) {
  if (typeof tree?.isLiveStream === 'boolean') return tree.isLiveStream;
  return isLiveStreamCustomSource(tree?.customSource);
}

/**
 * Phase 1: live UI/API only for platform dev accounts in production builds.
 * Assigned hosts/panelists (non-dev) still get crew UI when the stream doc lists them.
 * Public/non-dev viewers see normal content (no IVS stage) until general release.
 */
export function isLiveStreamingDevOnlyActive() {
  return process.env.NODE_ENV === 'production';
}

export function canUseLiveStreamingUi(stream) {
  if (!stream) return false;
  if (!isLiveStreamingDevOnlyActive()) return true;
  if (UserAccount.isDevAccount) return true;
  if (stream.isCrew) return true;
  if (effectiveLiveStreamAccessMode(stream) === 'open') return true;
  // Invited events: content viewers get the live-event gate / audience UI (not TreeAnswerPage).
  return true;
}

/** Host-only modes have no panelists — meeting join behaves like invited-only. */
export function effectiveLiveStreamAccessMode(stream) {
  if (!stream) return 'invited';
  const mode = stream.broadcastMode;
  if (mode === 'solo' || mode === 'rtmp' || mode === 'youtube') return 'invited';
  return stream.accessMode === 'open' ? 'open' : 'invited';
}

/**
 * True when the public IVS broadcast is active. Used for LIVE badges on rails;
 * `meeting` (backstage / not yet broadcasting) does not qualify.
 */
export function isLiveStreamBroadcasting(stream) {
  return stream?.status === 'live';
}

/** True when the host started the public IVS broadcast (replay may exist). */
export function liveStreamWentLive(stream) {
  if (!stream) return false;
  if (stream.timeline?.liveStartedAt) return true;
  if (stream.broadcast?.ivsChannelArn) return true;
  if (stream.broadcastMode === 'youtube' && stream.status === 'live') return true;
  if (['replay_processing', 'replay_ready'].includes(stream.status)) return true;
  if (stream.status === 'ended') return !!stream.timeline?.liveEndedAt;
  return false;
}

/** Poll while a stream doc is active so status transitions (scheduled → live) update the UI. */
export function shouldAttemptLiveStreamLoad(contentId, stream) {
  if (!isLiveStreamingDevOnlyActive()) return true;
  if (stream === undefined) return true;
  if (stream) return true;
  if (UserAccount.isLoggedIn) return true;
  return false;
}

function resolveProbeUrl(playbackUrl, baseUrl) {
  if (!playbackUrl) return null;
  try {
    return new URL(playbackUrl, baseUrl || undefined).href;
  } catch {
    return playbackUrl;
  }
}

function parseFirstVariantUrl(masterText, masterUrl) {
  const lines = masterText.split('\n');
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].startsWith('#EXT-X-STREAM-INF')) {
      const next = lines[i + 1]?.trim();
      if (next && !next.startsWith('#')) {
        return resolveProbeUrl(next, masterUrl);
      }
    }
  }
  return null;
}

async function fetchHlsMediaPlaylistText(playbackUrl) {
  const masterUrl = resolveProbeUrl(playbackUrl);
  const masterRes = await fetch(masterUrl, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!masterRes.ok) return null;
  const masterText = await masterRes.text();
  if (!masterText.includes('#EXTM3U')) return null;

  if (!masterText.includes('#EXT-X-STREAM-INF')) {
    return masterText;
  }

  const variantUrl = parseFirstVariantUrl(masterText, masterUrl);
  if (!variantUrl) return null;
  const variantRes = await fetch(variantUrl, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!variantRes.ok) return null;
  return variantRes.text();
}

/** Latest EXT-X-MEDIA-SEQUENCE from the IVS HLS media playlist (null when unavailable). */
export async function fetchHlsMediaSequence(playbackUrl) {
  if (!playbackUrl) return null;
  try {
    const mediaText = await fetchHlsMediaPlaylistText(playbackUrl);
    if (!mediaText) return null;
    const match = mediaText.match(/#EXT-X-MEDIA-SEQUENCE:(\d+)/);
    return match ? Number(match[1]) : null;
  } catch {
    return null;
  }
}

/** Best-effort client probe — IVS HLS may not be CORS-fetchable in all environments. */
export async function probeLiveHlsPlaybackReady(playbackUrl) {
  if (!playbackUrl) return false;
  try {
    const mediaText = await fetchHlsMediaPlaylistText(playbackUrl);
    if (!mediaText || !mediaText.includes('#EXTM3U')) return false;
    return mediaText.includes('#EXTINF')
      && (mediaText.includes('.ts') || mediaText.includes('.m4s') || mediaText.includes('.cmfv'));
  } catch {
    return false;
  }
}
